import { Injectable } from '@angular/core';
import {
    KalDate,
    KalSnackbarService
} from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import isEqual from 'lodash-es/isEqual';
import {
    forkJoin,
    Observable,
    of
} from 'rxjs';
import {
    map,
    tap
} from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { UserState } from 'src/app/_user/store/user/user.state';
import { activityAdvancedRuleFragments } from 'src/app/board/activity/activity-shared/activity-catalog-rules/fragments/activity-advanced-rule.fragments';
import { advancedMotherRuleToXML } from 'src/app/_shared/services/advanced-rule/advanced-rule.xml';
import {
    arrayEmpty,
    arrayNotEmpty,
    compare
} from 'src/app/utils/array.utils';
import { recursiveString } from 'src/app/utils/string.utils';
import {
    ArrayOfKeyValueModelDto,
    CalculRegleAvanceeModelDto,
    ContenuRegleAvanceeModelDto,
    KeyValueModelDto,
    ModeleEnveloppeDto,
    MoteurCritereDto,
    MoteurOperateurDto,
    MoteurRegleModelDto,
    MoteurRegleTypeDateRefDto,
    MutationChargerRegleAvanceeArgs,
    MutationEnregistrerReglesAvanceesArgs,
    MutationGetIdEnveloppeRegleArgs,
    MutationGetListeEnveloppeModeleGeneriqueArgs,
    MutationGetListeModeleMoteurRegleGeneriqueArgs,
    MutationGetListeReglesParentArgs,
    MutationGetListesDonneesReglesAvanceesArgs,
    MutationGetListeTypeArrondiArgs,
    MutationTransformerRegleGuideeEnExpertArgs,
    ParametreRegleAvanceeModelDto,
    RegleAvanceeModelDto,
    RegleExpertModel,
    StatusDtoEnum,
    TypeAfpaEnum,
    TypeArrondiModel,
    TypesRegleEnum
} from 'src/graphql/generated-types';
import { constants } from 'src/environments/constants';
import { cloneDeep } from 'lodash-es';

const NUMBER_OF_MAX_RULE_NODE = 10;

export type CritereModel = MoteurCritereDto & { PossedeDesParametres: boolean };

export type AdvancedRuleRelatedLists = {
    criteriaList: CritereModel[],
    operatorList: MoteurOperateurDto[],
    ruleParamList: ParametreRegleAvanceeModelDto[],
    applicationRuleList: KeyValueModelDto[],
    dateTypeList: MoteurRegleTypeDateRefDto[]
};

export type ParameterModel = {
    IdCritere?: number,
    IdOperateur?: number,
    Valeur?: string,
    IdChamp?: number,
};

export type RuleComputation = {
    Critere?: CritereModel,
    Valeur?: number,
    Operateur?: MoteurOperateurDto,
    Parametres?: ParameterModel[],
    type: ComputationNodeType,
};

export type AdvancedRule = RegleAvanceeModelDto & {
    ReglesEnfants?: AdvancedRule[]
    RegleParent?: AdvancedRule,
    ParentheseOuvrante?: number,
    ParentheseFermante?: number,
    Critere?: MoteurCritereDto,
    IdCritere?: number,
    IdOperateurMolecule?: number,
    Valeur?: string | KalDate | number | boolean,
    ListeCalculs?: RuleComputation[],
    ListeCalculsSubvention?: RuleComputation[],
    ListeCalculsSubventionDefaut?: RuleComputation[],
    Parametres?: ParameterModel[],
    SubventionGroupe?: number,
    ListeCalculsSubventionGroupe?: RuleComputation[],
    SubventionDefautGroupe?: number,
    ListeCalculsSubventionDefautGroupe?: RuleComputation[],
    Ordre?: number,
};

export type AdvancedRuleContent = {
    ListeReglesPourApplication?: ArrayOfKeyValueModelDto;
    Regle: AdvancedRule;
};

export enum TypesOperateur {
    Logique = 'L',
    Comparaison = 'C',
    Arithmetique = 'A',
}

export enum DataTypes {
    List = 'LST',
    Integer = 'ENT',
    Text = 'TXT',
    Number = 'NUM',
    Boolean = 'BOO',
    Date = 'DAT'
}

export enum ComputationNodeType {
    Operateur = 'operateur',
    Critere = 'critere',
    Valeur = 'valeur',
}

@Injectable({
    providedIn: 'root'
})
export class AdvancedRuleService {

    criteriaList: CritereModel[] = [];

    operatorList: MoteurOperateurDto[] = [];

    ruleParamList: ParametreRegleAvanceeModelDto[] = [];

    applicationRuleList: KeyValueModelDto[] = [];

    dateTypeList: MoteurRegleTypeDateRefDto[] = [];

    roundingTypesList: TypeArrondiModel[] = [];

    complementaryEnvelopeList: ModeleEnveloppeDto[] = [];

    _guid = 0;

    private currentLine: AdvancedRule = { ParentheseOuvrante: 0, ParentheseFermante: 0 };

    private flattenedRules: AdvancedRule[] = [];

    constructor(private readonly graphqlService: GraphqlService,
                private readonly snackbarService: KalSnackbarService,
                private readonly store: Store) {
    }

    get guid(): string {
        return '' + ++this._guid;
    }

    get criteriaOfCriteriaList(): CritereModel[] {
        return this.criteriaList.filter(({IsCritere}) => IsCritere);
    }

    loadRelatedLists$(ruleType: TypesRegleEnum): Observable<AdvancedRuleRelatedLists> {
        return this.graphqlService.mutateOne<'GetListesDonneesReglesAvancees', MutationGetListesDonneesReglesAvanceesArgs>({
            mutation: gql`
                mutation GetListesDonneesReglesAvancees(
                    $contexte: ContextInfosInput
                    $typeDeRegle: TypesRegleEnum
                ) {
                    GetListesDonneesReglesAvancees(
                        contexte: $contexte
                        typeDeRegle: $typeDeRegle
                    ) {
                        ListeCriteres {
                            MoteurCritereDTO {
                                Champ {
                                    ListChampListeValeur {
                                        ChampListeValeurDTO {
                                            Id
                                            Valeur
                                            IsCloture
                                        }
                                    }
                                }
                                Code
                                Id
                                IsCritere
                                IsRegleAccessibilite
                                IsRegleEnveloppe
                                IsRegleQuotaNombre
                                IsRegleQuotaValeurFaciale
                                IsRegleSubvention
                                Libelle
                                LibelleVariableMoteur
                                OrdreSQL
                                OrdreSqlParam
                                RegleCalcul {
                                    CalculParent {
                                        Id
                                    }
                                    Critere {
                                        Id
                                    }
                                    Id
                                    IsGroupe
                                    Libelle
                                    ListeCalculsEnfants {
                                        MoteurCalculDTO {
                                            Id
                                        }
                                    }
                                    ListeMotParamCrit {
                                        MoteurParamCritereDTO {
                                            Id
                                        }
                                    }
                                    Operateur {
                                        Id
                                    }
                                    Ordre
                                    ValeurNumerique
                                }
                                TypeCritere
                                TypeDonnee {
                                    Code
                                    Id
                                    Libelle
                                }
                            }
                        }
                        ListeOperateurs {
                            MoteurOperateurDTO {
                                MustValidate
                                MyGuid
                                Status
                                Id
                                Libelle
                                ListeCriteres {
                                    MoteurParamCritereDTO {
                                        Id
                                    }
                                }
                                ListeRegles {
                                    MoteurRegleDTO {
                                        Id
                                    }
                                }
                                TypeOperateur
                                Valeur
                            }
                        }
                        ListeParametres {
                            ParametreRegleAvanceeModelDTO {
                                Id
                                IdCritere
                                Libelle
                                OccurenceMax
                                OccurenceMin
                            }
                        }
                        ListeReglesPourApplication {
                            KeyValueModelDTO {
                                Id
                                Valeur
                            }
                        }
                        ListeTypesDates {
                            MoteurRegleTypeDateRefDTO {
                                Code
                                Id
                                Libelle
                            }
                        }
                    }
                }
            `,
            variables: {
                typeDeRegle: ruleType
            }
        }).pipe(
            map(GetListesDonneesReglesAvancees => ({
                criteriaList: GetListesDonneesReglesAvancees.ListeCriteres?.MoteurCritereDTO || [],
                operatorList: GetListesDonneesReglesAvancees.ListeOperateurs?.MoteurOperateurDTO || [],
                ruleParamList: GetListesDonneesReglesAvancees.ListeParametres?.ParametreRegleAvanceeModelDTO || [],
                applicationRuleList: GetListesDonneesReglesAvancees.ListeReglesPourApplication?.KeyValueModelDTO || [],
                dateTypeList: GetListesDonneesReglesAvancees.ListeTypesDates?.MoteurRegleTypeDateRefDTO || [],
            })),
            map(({criteriaList, operatorList, ruleParamList, applicationRuleList, dateTypeList}) => ({
                criteriaList: criteriaList.sort(compare('Libelle', 'asc'))?.map<CritereModel>(criterion => ({
                        ...criterion,
                        PossedeDesParametres: ruleParamList.some(({Id}) => Id === criterion.Id)
                    })),
                operatorList: operatorList.sort(compare('TypeOperateur', 'asc')),
                ruleParamList: ruleParamList.sort(compare('Libelle', 'asc')),
                applicationRuleList,
                dateTypeList: dateTypeList.sort(compare('Libelle', 'asc')),
            })),
            tap(({criteriaList, operatorList, ruleParamList, applicationRuleList, dateTypeList}) => {
                this.criteriaList = criteriaList.sort(compare('Libelle'));
                this.operatorList = operatorList;
                this.ruleParamList = ruleParamList;
                this.applicationRuleList = applicationRuleList;
                this.dateTypeList = dateTypeList;
            })
        );
    }

    getRoundingTypesList$(): Observable<TypeArrondiModel[]> {
        return this.graphqlService.mutateOne<'GetListeTypeArrondi', MutationGetListeTypeArrondiArgs>({
            mutation: gql`
                mutation GetListeTypeArrondi($contexte: ContextInfosInput) {
                    GetListeTypeArrondi(contexte: $contexte) {
                        TypeArrondiModel {
                            Code
                            IdTypeArrondi
                            TypeArrondi
                            TypeSP
                            Valeur
                        }
                    }
                }
            `
        }).pipe(
            map(GetListeTypeArrondi => GetListeTypeArrondi?.TypeArrondiModel || []),
            tap(roundingTypesList => this.roundingTypesList = roundingTypesList)
        );
    }

    getAdvancedInteractivityRuleListByRuleType$(ruleType: TypesRegleEnum): Observable<MoteurRegleModelDto[]> {
        let action = of([]);

        switch (ruleType) {
            case TypesRegleEnum.Accessibilite:
                action = this.getAdvancedInteractivityRuleList$(constants.rulesType.acc);
                break;
            case TypesRegleEnum.Subvention:
                action = this.getAdvancedInteractivityRuleList$(constants.rulesType.subv);
                break;
            case TypesRegleEnum.Enveloppe:
                action = this.getAdvancedInteractivityRuleList$(constants.rulesType.lim, 'M');
                break;
            case TypesRegleEnum.Quota:
            case TypesRegleEnum.QuotaFaciale:
            case TypesRegleEnum.QuotaNombre:
                action = forkJoin([
                    this.getAdvancedInteractivityRuleList$(constants.rulesType.lim, 'N'),
                    this.getAdvancedInteractivityRuleList$(constants.rulesType.lim, 'F')
                ]).pipe(
                    map(arrays => arrays.flat())
                );
                break;
        }

        return action.pipe(
            map(interactivityRules => interactivityRules.sort(compare('LibelleRegle', 'asc')))
        );
    }

    getAdvancedInteractivityRuleList$(TypeRegle: string, TypeValeurLimite: string = null): Observable<MoteurRegleModelDto[]> {
        return this.graphqlService.mutateOne<'GetListeModeleMoteurRegleGenerique', MutationGetListeModeleMoteurRegleGeneriqueArgs>({
            mutation: gql`

                mutation GetListeModeleMoteurRegleGenerique(
                    $contexte: ContextInfosInput,
                    $critere: CriteresMoteurRegleDTOInput
                ) {
                    GetListeModeleMoteurRegleGenerique(
                        contexte: $contexte
                        critere: $critere
                    ) {
                        MoteurRegleModelDTO {
                            Id
                            IdCE
                            LibelleRegle
                            TypeRegle
                            TypeValeurLimite
                            IsCloture
                            IsRegleAvancee
                        }
                    }
                }
            `,
            variables: {
                critere: {
                    IsTransverse: true,
                    TakeReglesAvancees: true,
                    IdCECurrentOuCEGlobal: this.store.selectSnapshot(UserState.currentDelegation).Id,
                    TypeRegle,
                    TypeValeurLimite
                }
            }
        }).pipe(
            map(GetListeModeleMoteurRegleGenerique => GetListeModeleMoteurRegleGenerique?.MoteurRegleModelDTO || [])
        );
    }

    getComplementaryEnvelopeList(): Observable<ModeleEnveloppeDto[]> {
        return this.graphqlService.mutateOne<'GetListeEnveloppeModeleGenerique', MutationGetListeEnveloppeModeleGeneriqueArgs>({
            mutation: gql`
                mutation GetListeEnveloppeModeleGenerique(
                    $contexte: ContextInfosInput
                    $critere: CriteresEnveloppeQuotaDTOInput
                ) {
                    GetListeEnveloppeModeleGenerique(
                        contexte: $contexte
                        critere: $critere
                    ) {
                        ModeleEnveloppeDTO {
                            Id
                            Libelle
                        }
                    }
                }
            `,
            variables: {
                critere: {
                    IsEnvComplementaire: true,
                    IsCloture: false
                }
            }
        }).pipe(
            map(GetListeEnveloppeModeleGenerique => GetListeEnveloppeModeleGenerique?.ModeleEnveloppeDTO || []),
            tap(complementaryEnvelopeList => this.complementaryEnvelopeList = complementaryEnvelopeList)
        );
    }

    saveAdvancedRule$(rule: ContenuRegleAvanceeModelDto): Observable<number> {
        return this.graphqlService.mutateOne<'EnregistrerReglesAvancees', MutationEnregistrerReglesAvanceesArgs>({
            mutation: gql`
                mutation EnregistrerReglesAvancees(
                    $contexte: ContextInfosInput
                    $rawXML: String
                ) {
                    EnregistrerReglesAvancees(
                        contexte: $contexte
                        rawXML: $rawXML
                    )
                }
            `,
            variables: {
                rawXML: advancedMotherRuleToXML(rule.Regle)
            }
        }).pipe(tap(()=> {
            this.snackbarService.open({
                title: `La règle "${rule.Regle.Libelle}" a bien été enregistrée.`
            });
        }));
    }

    normalize(rule: AdvancedRule & { TypeSP: string, Precision: number, TypeArrondi: string, IsSubventionEnveloppeComplementaire: boolean, IdEnveloppeComplementaire: number}): RegleAvanceeModelDto {
        let parenthesisCount = 0;

        const addChildToGroup = (child: RegleAvanceeModelDto, group: RegleAvanceeModelDto): void => {
            if (!group.ReglesEnfants) {
                group.ReglesEnfants = { RegleAvanceeModelDTO: [] };
            }

            child.RegleParent = {
                MyGuid: group.MyGuid,
                Status: StatusDtoEnum.Referenced,
            };

            group.ReglesEnfants.RegleAvanceeModelDTO.push(child);
        };

        const {
            Id,
            Libelle,
            TypeRegle,
            IsTransverse,
            IsForcageAutorise,
            IsVenteSansSubAutorise,
            IsComplementaire,
            IsSubventionEnveloppeComplementaire,
            IsSubChoisie,
            IdEnveloppeComplementaire,
            IdEnveloppe,
            TypeSP,
            TypeArrondi,
            Precision
        } = rule || {};

        let rootGroup: RegleAvanceeModelDto = {
            MyGuid: this.guid,
            Id: Id || 0,
            Status: !Id || (!!Id && (TypesRegleEnum.Quota || TypesRegleEnum.QuotaFaciale || TypesRegleEnum.QuotaNombre))
                ? StatusDtoEnum.Inserted
                : StatusDtoEnum.Updated,
            Libelle,
            TypeRegle,
            IsGroupe: true,
            IsTransverse,
            IsForcageAutorise,
            IsVenteSansSubAutorise,
            IsComplementaire: IsComplementaire || IsSubventionEnveloppeComplementaire,
            IsSubChoisie,
            IdEnveloppe: IsSubventionEnveloppeComplementaire ? IdEnveloppeComplementaire : IdEnveloppe,
            IdOperateur: 0,
            IdTypeDate: 0,
            IdTypeArrondi: this.roundingTypesList?.find((roundModel: TypeArrondiModel) =>
                roundModel.TypeSP === TypeSP
                && roundModel.TypeArrondi === TypeArrondi
                && roundModel.Valeur === Precision
            )?.IdTypeArrondi || null,
        };
        let currentGroup = rootGroup;

        rule.ReglesEnfants?.forEach(childRule => {
            const {
                ParentheseOuvrante,
                ParentheseFermante,
                IdOperateur,
                ListeCalculsSubventionGroupe,
                SubventionGroupe,
                ListeCalculsSubventionDefautGroupe,
                SubventionDefautGroupe,
                IdTypeDate,
                Jours,
                Mois
            } = childRule || {};

            parenthesisCount = ParentheseOuvrante - ParentheseFermante;

            if (!currentGroup.IdOperateur) {
                // S'il n'y a pas d'opérateur on prend celui de la ligne en cours.
                currentGroup.IdOperateur = IdOperateur;
            } else if (currentGroup.IdOperateur !== IdOperateur) {
                //Si l'opérateur est différent de la ligne, on crée une règle parent.
                if (!currentGroup.RegleParent) {
                    const newParentGroup: RegleAvanceeModelDto = {
                        ...currentGroup,
                        IdOperateur,
                        IdTypeDate: 0,
                        ReglesEnfants: null,
                        RegleParent: currentGroup.RegleParent || {
                            MyGuid: rootGroup.MyGuid,
                            Status: StatusDtoEnum.Referenced,
                        },
                        MyGuid: this.guid,
                    };

                    addChildToGroup({
                        ...cloneDeep(currentGroup),
                        Id: 0
                    }, newParentGroup);

                    rootGroup = newParentGroup;
                    currentGroup = rootGroup;
                } else {
                    let parentRule = this.findRuleReccur(currentGroup.MyGuid, rootGroup) || rootGroup;

                    const childRule = {
                        ...cloneDeep(currentGroup),
                        MyGuid: this.guid,
                        RegleParent: {
                            MyGuid: parentRule.MyGuid,
                            Status: StatusDtoEnum.Referenced
                        }
                    };
                    childRule.ReglesEnfants.RegleAvanceeModelDTO.forEach(r => {
                        r.RegleParent = {
                            MyGuid: childRule.MyGuid,
                            Status: StatusDtoEnum.Referenced
                        };
                    });

                    parentRule.ReglesEnfants.RegleAvanceeModelDTO = [childRule];
                    parentRule.IdOperateur = IdOperateur;
                    currentGroup = parentRule;
                }
            }

            if (parenthesisCount > 0) {
                for (let i = 0; i < parenthesisCount; i++) {
                    const newParenthesisGroup = {
                        MyGuid: this.guid,
                        Libelle,
                        TypeRegle,
                        IsTransverse,
                        IsGroupe: true,
                        RegleParent: {
                            MyGuid: currentGroup.MyGuid,
                            Status: StatusDtoEnum.Referenced,
                        },
                        Id: 0,
                        IdOperateur: 0,
                        IdTypeDate: 0
                    };

                    addChildToGroup(newParenthesisGroup, currentGroup);
                    currentGroup = newParenthesisGroup;
                }
            }

            const leafRule = this.createLeafRule(childRule, currentGroup);
            addChildToGroup(leafRule, currentGroup);

            if (arrayNotEmpty(ListeCalculsSubventionGroupe)) {
                currentGroup.CalculSubvention = this.createComputationsFromList(ListeCalculsSubventionGroupe);
            } else if (SubventionGroupe) {
                currentGroup.Subvention = SubventionGroupe;
            }

            if (arrayNotEmpty(ListeCalculsSubventionDefautGroupe)) {
                currentGroup.CalculSubventionDefaut = this.createComputationsFromList(ListeCalculsSubventionDefautGroupe);
            } else if (SubventionDefautGroupe) {
                currentGroup.SubventionDefaut = SubventionDefautGroupe;
            }

            if ((!!currentGroup.CalculSubvention || !!currentGroup.CalculSubventionDefaut) && !!IdTypeDate) {
                currentGroup.IdTypeDate = IdTypeDate;
                currentGroup.Jours = Jours;
                currentGroup.Mois = Mois;
            }

            for (let i = 0; i < childRule.ParentheseFermante; i++) {
                if (currentGroup.RegleParent) {
                    currentGroup = this.findRuleReccur(currentGroup.RegleParent.MyGuid, rootGroup) || rootGroup;
                }
            }
        });

        rootGroup.RegleParent = null;
        rootGroup.Id = Id || 0;

        return rootGroup;
    }

    getAdvancedRule$(ruleId: number, ruleType: TypesRegleEnum): Observable<AdvancedRuleContent> {
        if (!ruleId) {
            return of<AdvancedRuleContent>({
                Regle: {
                    TypeRegle: ruleType === TypesRegleEnum.Quota ? TypesRegleEnum.QuotaNombre : ruleType
                }
            });
        }

        return this.graphqlService.mutateOne<'ChargerRegleAvancee', MutationChargerRegleAvanceeArgs>({
            mutation: gql`
                mutation ChargerRegleAvancee(
                    $contexte: ContextInfosInput
                    $idRegle: Float
                ) {
                    ChargerRegleAvancee(
                        contexte: $contexte
                        idRegle: $idRegle
                    ) {
                        ListeReglesPourApplication {
                            KeyValueModelDTO {
                                Id
                                Valeur
                            }
                        }
                        Regle {
                            ...RegleAvanceeFragment
                            RegleParent {
                                Id
                            }
                            ${recursiveString('ReglesEnfants {\n', '\n}\n,}', 'RegleAvanceeModelDTO {\n ...RegleAvanceeFragment\nRegleParent {\nId\n}', NUMBER_OF_MAX_RULE_NODE)}
                        }
                    }
                }

                ${activityAdvancedRuleFragments.advancedComputing}
                ${activityAdvancedRuleFragments.advancedRule}
            `,
            variables: {
                idRegle: ruleId
            }
        }).pipe(
            map(rule => this.flattenChildrenRules(rule))
        );
    }

    flattenChildrenRules(advancedRuleContent: ContenuRegleAvanceeModelDto): AdvancedRuleContent {
        const rootRule = {...advancedRuleContent.Regle} as AdvancedRule;

        this.flattenedRules = [];
        this.flattenChildrenRulesReccur(rootRule);
        rootRule.ReglesEnfants = this.flattenedRules;

        return {
            ListeReglesPourApplication: advancedRuleContent.ListeReglesPourApplication || null,
            Regle: rootRule
        };
    }

    /**
     * Utilisé par les activités Médiathèque, Ressource, Prêts, (p't'être épargne) quand on passe au mode avancé.
     * @param ruleId
     */
    transformSimpleRuleToAdvanced$(ruleId: number): Observable<string> {
        return this.graphqlService.mutateOne<'TransformerRegleGuideeEnExpert', MutationTransformerRegleGuideeEnExpertArgs>({
            mutation: gql`
                mutation TransformerRegleGuideeEnExpert($contexte: ContextInfosInput, $idRegle: Float) {
                    TransformerRegleGuideeEnExpert(contexte: $contexte, idRegle: $idRegle)
                }
            `,
            variables: {
                idRegle: +ruleId
            }
        });
    }

    getParentRules$({
                        parentId,
                        parentType
                    }: { parentId: number, parentType: TypeAfpaEnum }): Observable<RegleExpertModel[]> {
        return this.graphqlService.mutateOne<'GetListeReglesParent', MutationGetListeReglesParentArgs>({
            mutation: gql`
                # ArrayOfRegleExpertModel
                mutation GetListeReglesParent(
                    $contexte: ContextInfosInput
                    $idElement: Float
                    $typeElement: TypeAFPAEnum
                ) {
                    GetListeReglesParent(
                        contexte: $contexte
                        idElement: $idElement
                        typeElement: $typeElement
                    ) {
                        RegleExpertModel {
                            IdRegle
                            IsAccessibilite
                            IsEnveloppe
                            IsHerite
                            IsModeExpert
                            IsQuota
                            IsRegleHeritage
                            IsRestantACharge
                            IsSubvention
                            IsSubventionComplementaire
                            Libelle
                            NeedsToBeDeleted
                        }
                    }
                }
            `,
            variables: {
                idElement: parentId,
                typeElement: parentType
            }
        }).pipe(
            map(GetListeReglesParent => (GetListeReglesParent?.RegleExpertModel || []).filter(({
                                                                                                   IsHerite,
                                                                                                   IsAccessibilite,
                                                                                                   IsQuota
                                                                                               }) => !IsHerite && (IsAccessibilite || IsQuota)))
        );
    }

    getComplementaryRuleEnvelopeId$(ruleId: number): Observable<number> {
        return this.graphqlService.mutateOne<'GetIdEnveloppeRegle', MutationGetIdEnveloppeRegleArgs>({
            mutation: gql`
                mutation GetIdEnveloppeRegle(
                    $contexte: ContextInfosInput,
                    $idRegle: Float
                ) {
                    GetIdEnveloppeRegle(
                        contexte: $contexte,
                        idRegle: $idRegle
                    )
                }
            `,
            variables: {
                idRegle: ruleId
            }
        });
    }

    private flattenChildrenRulesReccur(rule: AdvancedRule, rootRule: AdvancedRule = null): void {
        let parentRule: RegleAvanceeModelDto = null;
        if(!rootRule) {
            rootRule = rule;
        }

        if(!!rule.RegleParent) {
            if (rule.RegleParent.Id) {
                parentRule = this.findRuleReccur(rule.RegleParent.Id.toString(), rootRule, 'Id');
            } else if (rule.RegleParent.MyGuid) {
                parentRule = this.findRuleReccur(rule.RegleParent.MyGuid, rootRule, 'MyGuid');
            }
        }

        if (parentRule && rule.Id !== parentRule.ReglesEnfants?.RegleAvanceeModelDTO?.[0]?.Id) {
            this.currentLine.IdOperateur = this.operatorList.find(({Id}) => Id === rule.RegleParent.IdOperateur)?.Id;
        }

        if (rule?.IsGroupe) {

            if (!!parentRule) {
                this.currentLine.ParentheseOuvrante += 1;
            }

            rule.ReglesEnfants?.RegleAvanceeModelDTO?.forEach(childRule => {
                this.flattenChildrenRulesReccur(childRule as AdvancedRule, rootRule);
            });

            if (this.flattenedRules.length > 0) {
                if (!!parentRule) {
                    this.flattenedRules[this.flattenedRules.length - 1].ParentheseFermante += 1;
                }

                if (!!rule.IdRegleApplication) {
                    this.flattenedRules[this.flattenedRules.length - 1].IdRegleApplication;
                }

                if (rule.IdRegleApplication) {
                    this.flattenedRules[this.flattenedRules.length - 1].IdRegleApplication = rule.IdRegleApplication;
                }

                if (rule.IdRegleNonApplication) {
                    this.flattenedRules[this.flattenedRules.length - 1].IdRegleNonApplication = rule.IdRegleNonApplication;
                }
            }

        } else {

            this.fillRule(rule);

            if (!!parentRule && parentRule.ReglesEnfants?.RegleAvanceeModelDTO?.slice(-1)?.[0].Id === rule.Id) {
                if (parentRule.CalculSubvention?.Id > 0) {
                    this.currentLine.ListeCalculsSubventionGroupe = this.getFilledComputations(parentRule.CalculSubvention);
                } else {
                    this.currentLine.SubventionGroupe = parentRule.Subvention || null;
                }
                if (parentRule.CalculSubventionDefaut?.Id > 0) {
                    this.currentLine.ListeCalculsSubventionDefautGroupe = this.getFilledComputations(parentRule.CalculSubventionDefaut);
                } else {
                    this.currentLine.SubventionDefautGroupe = parentRule.SubventionDefaut || null;
                }
            }

            this.flattenedRules.push(this.currentLine);
            this.currentLine = { ParentheseOuvrante: 0, ParentheseFermante: 0 };

        }

    }

    private fillRule(rule: RegleAvanceeModelDto): void {
        const Molecule = rule.Molecule;
        const criterionId = Molecule?.Critere?.Id || 0;

        this.currentLine.Id = rule.Id;
        this.currentLine.IdTypeDate = rule.IdTypeDate;
        this.currentLine.Mois = rule.Mois;
        this.currentLine.Jours = rule.Jours;
        this.currentLine.Subvention = rule.Subvention;
        this.currentLine.SubventionDefaut = rule.SubventionDefaut;
        this.currentLine.IdRegleApplication = rule.IdRegleApplication;
        this.currentLine.IdRegleNonApplication = rule.IdRegleNonApplication;
        this.currentLine.CalculSubvention = rule.CalculSubvention;
        this.currentLine.CalculSubventionDefaut = rule.CalculSubventionDefaut;

        this.currentLine.IdCritere = criterionId;

        if (criterionId > 0) {
            this.currentLine.Critere = this.criteriaList.find(({Id}) => criterionId === Id);
        }

        this.currentLine.IdOperateurMolecule = Molecule?.IdOperateur || null;

        this.currentLine.Valeur = this.currentLine.Critere?.TypeDonnee?.Code === DataTypes.List
            ? Molecule?.IdChamp
            : Molecule?.Valeur || '0';

        if (Molecule?.CalculValeur?.Id > 0) {
            this.currentLine.ListeCalculs = this.getFilledComputations(Molecule.CalculValeur);
        }

        if (arrayNotEmpty(Molecule?.Critere?.Parametres?.MoleculeRegleAvanceeModelDTO)) {
            this.currentLine.Parametres = Molecule?.Critere?.Parametres?.MoleculeRegleAvanceeModelDTO.map(p => ({
                ...p,
                IdCritere: p.Critere?.Id,
            }));
        }

        if (this.currentLine.CalculSubvention?.Id > 0 || arrayNotEmpty(this.currentLine.CalculSubvention?.CalculsEnfants?.CalculRegleAvanceeModelDTO)) {
            this.currentLine.ListeCalculsSubvention = this.getFilledComputations(this.currentLine.CalculSubvention);
        }
        if (this.currentLine.CalculSubventionDefaut?.Id > 0) {
            this.currentLine.ListeCalculsSubventionDefaut = this.getFilledComputations(this.currentLine.CalculSubventionDefaut);
        }

    }

    private getFilledComputations(computation: CalculRegleAvanceeModelDto): RuleComputation[] {
        if (!computation.IsGroupe || arrayEmpty(computation.CalculsEnfants?.CalculRegleAvanceeModelDTO)) {

            if (!!computation.Valeur) {
                return [{Valeur: +computation.Valeur, type: ComputationNodeType.Valeur}];
            }
            const criterion = computation?.Critere;

            if (!criterion?.Id) {
                return [];
            }

            return [{
                Critere: this.criteriaList.find(({Id}) => criterion.Id === Id),
                type: ComputationNodeType.Critere,
                Parametres: criterion.Parametres?.MoleculeRegleAvanceeModelDTO?.map(({
                                                                                         Critere,
                                                                                         IdOperateur,
                                                                                         Valeur,
                                                                                         IdChamp
                                                                                     }) => {

                    return {
                        IdChamp,
                        IdOperateur,
                        IdCritere: Critere.Id,
                        Valeur
                    };
                })
            }
            ];
        }

        const hasParent = !!computation.CalculParent;

        const children = computation.CalculsEnfants?.CalculRegleAvanceeModelDTO;

        return [
            ...hasParent ? [{
                Operateur: {
                    Id: 0,
                    Libelle: '(',
                    Valeur: '(',
                    TypeOperateur: TypesOperateur.Arithmetique
                },
                type: ComputationNodeType.Operateur,
            }] : [],

            ...children.sort(compare('Ordre', 'asc'))
                .map((c, index) => index === children.length - 1
                    ? this.getFilledComputations(c)
                    : [
                        ...this.getFilledComputations(c),
                        {
                            Operateur: this.operatorList.find(({Id}) => Id === computation.IdOperateur),
                            type: ComputationNodeType.Operateur
                        }
                    ])
                .flat(),

            ...hasParent ? [{
                Operateur: {
                    Id: 0,
                    Libelle: ')',
                    Valeur: ')',
                    TypeOperateur: TypesOperateur.Arithmetique,
                },
                type: ComputationNodeType.Operateur,
            }] : []
        ];
    }

    private createLeafRule(rule: AdvancedRule, parentRule: RegleAvanceeModelDto): RegleAvanceeModelDto {
        const {
            IdTypeDate = 0,
            Mois = 0,
            Jours = 0,
            Subvention = 0,
            SubventionDefaut = 0,
            IdRegleApplication,
            IdRegleNonApplication,
            IdOperateurMolecule,
            IdCritere,
            Valeur,
            ListeCalculs,
            ListeCalculsSubventionDefaut,
            ListeCalculsSubvention,
            IdOperateur
        } = rule;

        let parameters = [];

        if (arrayNotEmpty(rule.Parametres)) {
            rule.Parametres.forEach((p) => {
                let molecule = {
                    Critere: {Id: p.IdCritere, MyGuid: this.guid},
                    IdChamp: +p.IdChamp || 0,
                    IdOperateur: p.IdOperateur,
                    Valeur: p.Valeur
                };

                parameters.push(molecule);
            });
        }

        const criterionType = this.criteriaList.find(({Id}) => Id === IdCritere)?.TypeDonnee;

        let ruleModel: RegleAvanceeModelDto = {
            TypeRegle: parentRule.TypeRegle,
            IsTransverse: parentRule.IsTransverse,
            Libelle: parentRule.Libelle,
            Molecule: {
                MyGuid: this.guid,
                Critere: {
                    MyGuid: this.guid,
                    Id: IdCritere
                },
                IdChamp: criterionType?.Code === DataTypes.List ? +Valeur : null,
                Valeur: (!!criterionType && criterionType.Code !== DataTypes.List) ? ('' + Valeur) : null,
                IdOperateur: IdOperateurMolecule,
                CalculValeur: this.createComputationsFromList(ListeCalculs)
            },
            IdTypeDate, Jours, Mois,
            Subvention,
            SubventionDefaut,
            IdRegleApplication: IdRegleApplication || null,
            IdRegleNonApplication: IdRegleNonApplication || null,
            CalculSubvention: this.createComputationsFromList(ListeCalculsSubvention),
            CalculSubventionDefaut: this.createComputationsFromList(ListeCalculsSubventionDefaut),
            RegleParent: {
                MyGuid: parentRule.MyGuid,
                Status: StatusDtoEnum.Referenced
            },
            Id: 0,
            IdOperateur: IdOperateur || 0,
            MyGuid: this.guid,
        };

        if (arrayNotEmpty(parameters)) {
            ruleModel.Molecule.Critere.Parametres = {MoleculeRegleAvanceeModelDTO: parameters};
        }

        return ruleModel;
    }

    private createComputationsFromList(computationList: RuleComputation[]): CalculRegleAvanceeModelDto {
        if (arrayEmpty(computationList)) {
            return null;
        }

        const MyGuid = this.guid;

        let currentNodeGroup: CalculRegleAvanceeModelDto = {
            IsGroupe: true,
            Ordre: 1,
            MyGuid
        };
        let baseNode = currentNodeGroup;

        const nodeList: CalculRegleAvanceeModelDto[] = [currentNodeGroup];

        const nextOrder = (): number => (currentNodeGroup.CalculsEnfants?.CalculRegleAvanceeModelDTO?.length || 0) + 1;

        const addNodeToGroup = (node: CalculRegleAvanceeModelDto, group: CalculRegleAvanceeModelDto): void => {
            if (arrayEmpty(group.CalculsEnfants?.CalculRegleAvanceeModelDTO)) {
                group.CalculsEnfants = {CalculRegleAvanceeModelDTO: []};
            }

            if (node.CalculParent && node.CalculParent.MyGuid === MyGuid) {
                node.CalculParent = {
                    Status: StatusDtoEnum.Referenced,
                    MyGuid
                };
            }

            group.CalculsEnfants.CalculRegleAvanceeModelDTO.push(node);
            nodeList.push(node);
        };

        // Recherche récursive pour retrouver le parent à partir de la base
        const searchingParent = (rule: CalculRegleAvanceeModelDto): CalculRegleAvanceeModelDto => {
            let found: CalculRegleAvanceeModelDto = null;
            rule.CalculsEnfants?.CalculRegleAvanceeModelDTO?.forEach(r => {
                if (r.MyGuid === currentNodeGroup.CalculParent.MyGuid) {
                    found = r;
                    return;
                }
                const r2 = searchingParent(r);
                if (!!r2) {
                    found = r2;
                    return;
                }
            });
            return found;
        };

        computationList.forEach((computation) => {
            if (computation?.Operateur?.Id > 0) {
                if ((currentNodeGroup.IdOperateur || 0) < 1) {
                    currentNodeGroup.IdOperateur = computation.Operateur.Id;
                } else if (currentNodeGroup.IdOperateur !== computation.Operateur.Id) {

                    if (!currentNodeGroup.CalculParent) {
                        const guid = this.guid;

                        baseNode.CalculParent = {
                            Status: StatusDtoEnum.Referenced,
                            MyGuid: guid
                        };
                        let newGroupe: CalculRegleAvanceeModelDto = {
                            MyGuid: guid,
                            IsGroupe: true,
                            IdOperateur: computation.Operateur.Id,
                            CalculParent: null,
                            Ordre: 1,
                        };

                        addNodeToGroup(currentNodeGroup, newGroupe);
                        baseNode = currentNodeGroup = newGroupe;
                    } else if (currentNodeGroup.CalculParent.IdOperateur === computation.Operateur.Id) {
                        currentNodeGroup = baseNode.MyGuid === currentNodeGroup.CalculParent.MyGuid ? baseNode : searchingParent(baseNode);
                    } else {
                        let newGroupe: CalculRegleAvanceeModelDto = {
                            MyGuid: this.guid,
                            CalculParent: {
                                MyGuid: currentNodeGroup.CalculParent.MyGuid,
                                Status: StatusDtoEnum.Referenced
                            },
                            Ordre: currentNodeGroup.Ordre,
                            IsGroupe: true,
                            IdOperateur: computation.Operateur.Id,
                        };

                        // On supprime le lien du parent du groupe courant vers le groupe courant.
                        // groupeCourant.CalculParent.CalculsEnfants.Remove(groupeCourant);
                        const currentNodeGroupParent = searchingParent(baseNode);

                        if (currentNodeGroupParent) {
                            currentNodeGroupParent.CalculsEnfants = {
                                CalculRegleAvanceeModelDTO: currentNodeGroupParent.CalculsEnfants?.CalculRegleAvanceeModelDTO?.filter(g => !isEqual(g, currentNodeGroup)) || []
                            };
                            // On ajoute un lien sur le parent du groupe courant vers le nouveau groupe.
                            currentNodeGroupParent.CalculsEnfants.CalculRegleAvanceeModelDTO.push(newGroupe);

                            // On ajoute un lien de parenté au groupe courant vers le nouveau groupe.
                            currentNodeGroup.CalculParent = {
                                MyGuid: newGroupe.MyGuid,
                                Status: StatusDtoEnum.Referenced
                            };

                            // Le groupe courant va devenir le premier enfant du nouveau groupe.
                            currentNodeGroup.Ordre = 1;
                            // On ajoute le groupe courant aux enfants du nouveau groupe.
                            addNodeToGroup(currentNodeGroup, newGroupe);
                            // Le nouveau groupe peut enfin devenir le nouveau groupe courant.
                            currentNodeGroup = newGroupe;
                        }
                    }
                }
            } else if (!!computation.Operateur?.Libelle) {
                const operatorLabel = computation.Operateur?.Libelle || null;

                if (operatorLabel === '(') {
                    const newGroup = {
                        IsGroupe: true,
                        CalculParent: {
                            Status: StatusDtoEnum.Referenced,
                            MyGuid: currentNodeGroup.MyGuid
                        },
                        Ordre: nextOrder(),
                        MyGuid: this.guid,
                    };

                    addNodeToGroup(newGroup, currentNodeGroup);

                    currentNodeGroup = newGroup;

                } else if (operatorLabel === ')' && !!currentNodeGroup.CalculParent) {
                    currentNodeGroup = baseNode.MyGuid === currentNodeGroup.CalculParent.MyGuid ? baseNode : searchingParent(baseNode);
                }

            } else if (computation.Critere?.Id > 0) {
                let newCalcul: CalculRegleAvanceeModelDto = {
                    CalculParent: {
                        Status: StatusDtoEnum.Referenced,
                        MyGuid: currentNodeGroup.MyGuid
                    },
                    Ordre: nextOrder(),
                    Critere: {
                        Id: computation.Critere.Id,
                        Parametres: {
                            MoleculeRegleAvanceeModelDTO: []
                        }
                    },
                };

                if (arrayNotEmpty(computation.Parametres)) {
                    computation.Parametres.forEach((p) => {
                        let newParam = {
                            Critere: {
                                Id: p.IdCritere
                            },
                            IdOperateur: p.IdOperateur,
                            Valeur: !!p.Valeur ? p.Valeur : '' + p.IdChamp,
                            IdChamp: p.IdChamp,
                        };

                        newCalcul.Critere.Parametres.MoleculeRegleAvanceeModelDTO.push(newParam);
                    });
                }

                addNodeToGroup(newCalcul, currentNodeGroup);

            } else {
                addNodeToGroup({
                    CalculParent: {
                        Status: StatusDtoEnum.Referenced,
                        MyGuid: currentNodeGroup.MyGuid
                    },
                    Ordre: nextOrder(),
                    Valeur: +computation.Valeur,
                    MyGuid: this.guid,
                }, currentNodeGroup);
            }
        });

        return baseNode;
    }

    private findRuleReccur(searchValue: string, rootGroup: RegleAvanceeModelDto, searchProperty = 'MyGuid'): RegleAvanceeModelDto | null {
        if (!rootGroup) {
            return null;
        }

        if (rootGroup[searchProperty].toString() === searchValue) {
            return rootGroup;
        }

        let rule: RegleAvanceeModelDto | null = null;

        rootGroup.ReglesEnfants?.RegleAvanceeModelDTO?.forEach(r => {
            if (!rule) {
                rule = this.findRuleReccur(searchValue, r, searchProperty);

                if (!rule && r[searchProperty].toString() === searchValue) {
                    rule = r;
                }
            }
        });

        return rule;
    }
}
